import React from "react";

import { emailRegex } from "../../../constants/regex";
import { ModalContext } from "../../../libs/ModalProvider";
import { IStudentParam } from "../../../managerStore";
import useCurrentYear from "../../../hooks/manager/useCurrentYear";
import Confirm from "../../../molecules/Confirm";
import { IClass, IStudentPlan, ISchoolManager, IStudent } from "../../../state";

import styles from "./StudentForm.module.scss";

export interface StudentFormProps {
  title: string;
  calling: boolean;
  error: string;
  student?: IStudent;
  classes?: IClass[];
  next_classes?: IClass[];
  school: ISchoolManager;
  isNextYear: boolean;
  upsertStudent: (params: IStudentParam) => void;
  deleteStudent?: (id: number) => void;
  plans: IStudentPlan[];
}

const validates: { [key: string]: (value: any) => string } = {};

validates["first_name"] = val => {
  return val.first_name ? "" : "生徒の名を入力してください。";
};

validates["last_name"] = val => {
  return val.last_name ? "" : "生徒の姓を入力してください。";
};

validates["first_name_kana"] = val => {
  return val.first_name_kana ? "" : "生徒の名(カナ)を入力してください。";
};

validates["last_name_kana"] = val => {
  return val.last_name_kana ? "" : "生徒の姓(カナ)を入力してください。";
};

validates["next_grade"] = val => {
  return val.next_grade < val.current_grade
    ? "次の学年の指定に誤りがあります"
    : "";
};

validates["email"] = val => {
  if (!val.email?.length) return "";
  return emailRegex.test(val.email)
    ? ""
    : "正しいメールアドレスを入力してください。";
};

validates["guardian_email"] = val => {
  if (!val.guardian_email?.length) return "";
  return emailRegex.test(val.guardian_email)
    ? ""
    : "正しいメールアドレスを入力してください。";
};

export const StudentForm = (props: StudentFormProps) => {
  const ctx = React.useContext(ModalContext);
  const current_grade = !props.isNextYear
    ? sessionStorage.getItem("current_grade")
      ? parseInt(sessionStorage.getItem("current_grade") as string, 10)
      : 1
    : 0;
  const next_grade = current_grade + 1;
  const { currentYear, nextYear, nextYearShortLabel } = useCurrentYear();
  const entrance_year = !props.isNextYear
    ? currentYear - current_grade + 1
    : nextYear;

  const [state, setState] = React.useState({
    id: props.student ? props.student.id : 0,
    class_id: props.student ? props.student.class_id : 0,
    next_klass_id: props.student ? props.student.next_klass_id : 0,
    first_name: props.student ? props.student.first_name : "",
    last_name: props.student ? props.student.last_name : "",
    first_name_kana: props.student ? props.student.first_name_kana : "",
    last_name_kana: props.student ? props.student.last_name_kana : "",
    entrance_year: props.student ? props.student.entrance_year : entrance_year,
    current_grade: props.student ? props.student.current_grade : current_grade,
    g4s_enabled: props.student ? props.student.g4s_enabled : false,
    dlp_enabled: props.student
      ? props.student.dlp_enabled
      : props.school.total_dlp_registrable_num! > 0,
    next_grade: props.student ? props.student.next_grade : next_grade,
    student_no_in_school: props.student
      ? props.student.student_no_in_school
      : "",
    email: props.student ? props.student.email : "",
    guardian_email: props.student ? props.student.guardian_email : "",
    isSubmitted: false,
    errorMessages: {} as { [key: string]: string },
    plan_ids: props.student?.active_plans?.map(plan => plan.plan_id) ?? [],
  });

  validates["next_klass_id"] = val => {
    if (props.isNextYear)
      return val.next_klass_id
        ? ""
        : `生徒の${nextYearShortLabel}年度クラスを選択してください。`;
    return "";
  };

  validates["plan_ids"] = val => {
    if (
      props.school.use_case === "cram_school" &&
      !props.student?.plan_disabled
    ) {
      return val.plan_ids && val.plan_ids.length > 0
        ? ""
        : "お申し込みしたプランの中から選択してください。";
    }
    return "";
  };

  React.useEffect(() => {
    setState(s => {
      if (props.student) {
        return {
          ...s,
          id: props.student.id,
          class_id: props.student.class_id,
          next_klass_id: props.student.next_klass_id,
          first_name: props.student.first_name,
          last_name: props.student.last_name,
          first_name_kana: props.student.first_name_kana,
          last_name_kana: props.student.last_name_kana,
          student_no_in_school: props.student.student_no_in_school,
          email: props.student.email,
          guardian_email: props.student.guardian_email,
          entrance_year: props.student.entrance_year,
          current_grade: props.student.current_grade,
          next_grade: props.student.next_grade,
          g4s_enabled: props.student.g4s_enabled,
          dlp_enabled: props.student.dlp_enabled,
          plan_ids:
            props.student?.active_plans?.map(plan => plan.plan_id) ?? [],
        };
      } else {
        return { ...s, entrance_year };
      }
    });
  }, [props.student, entrance_year]);

  const onSubmit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const errorMessages = {} as { [key: string]: string };
      Object.keys(validates).forEach(key => {
        const ret = validates[key](state);
        if (ret) {
          errorMessages[key] = ret;
        }
      });
      if (Object.keys(errorMessages).length > 0) {
        setState(s => ({ ...s, errorMessages }));
        return;
      }
      const updateStudent = () => {
        props.upsertStudent({
          id: state.id,
          klass_id: state.class_id || null,
          next_klass_id: state.next_klass_id || null,
          last_name: state.last_name,
          first_name: state.first_name,
          last_name_kana: state.last_name_kana,
          first_name_kana: state.first_name_kana,
          student_no_in_school: state.student_no_in_school || "",
          entrance_year: state.entrance_year,
          current_grade: state.current_grade || null,
          next_grade: state.next_grade,
          g4s_enabled: state.g4s_enabled,
          dlp_enabled: state.dlp_enabled,
          email: state.email || null,
          guardian_email: state.guardian_email || null,
          plan_ids: state.plan_ids || null,
        });
      };
      if (!props.student?.plan_disabled && state.plan_ids.length > 0) {
        ctx.actions.showModal(
          <Confirm
            calling={props.calling}
            closeModal={() => {
              ctx.actions.closeModal();
            }}
            content={
              <>
                <p className="mb-0">
                  現在選択中のプランは、当月の請求対象となります。※翌月授業開始する場合は翌月に生徒登録を行ってください。
                </p>
              </>
            }
            variant="normal"
            confirmText="登録"
            callback={() => {
              updateStudent();
              ctx.actions.closeModal();
            }}
          />,
        );
      } else {
        updateStudent();
      }
    },
    [state, props, ctx.actions],
  );

  const entranceYearSelection = !props.isNextYear
    ? [-5, -4, -3, -2, -1, 0]
    : [1];
  const disabled =
    props.isNextYear &&
    props.student &&
    props.student.entrance_year <= currentYear;

  const onChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const key = e.currentTarget.name;
      const val = e.currentTarget.type.match(/select/)
        ? parseInt(e.currentTarget.value, 10)
        : e.currentTarget.type === "radio"
        ? e.currentTarget.value === "true"
          ? true
          : e.currentTarget.value === "false"
          ? false
          : parseInt(e.currentTarget.value, 10)
        : e.currentTarget.type === "checkbox"
        ? parseInt(e.currentTarget.value, 10) ===
          (state as { [key: string]: any })[key]
          ? 0
          : parseInt(e.currentTarget.value, 10)
        : e.currentTarget.value;
      const errorMessages = {} as { [key: string]: string };
      setState(prevState => {
        const args = { ...prevState, [key]: val };
        Object.keys(validates).forEach(k => {
          errorMessages[k] = validates[k](args);
        });
        return {
          ...args,
          errorMessages,
        };
      });
    },
    [state],
  );

  const onPlanChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value, checked } = e.currentTarget;
      let updatedValue: any;
      if (name === "plan_ids") {
        const parsedValue = parseInt(value, 10);
        updatedValue = checked
          ? [...(state.plan_ids || []), parsedValue]
          : (state.plan_ids || []).filter(pid => pid !== parsedValue);
      } else {
        updatedValue =
          parseInt(value, 10) === (state as { [key: string]: any })[name]
            ? 0
            : parseInt(value, 10);
      }

      const errorMessages = {} as { [key: string]: string };
      setState(prevState => {
        const args = { ...prevState, [name]: updatedValue };
        Object.keys(validates).forEach(k => {
          errorMessages[k] = validates[k](args);
        });
        return {
          ...args,
          errorMessages,
        };
      });
    },
    [state, setState],
  );

  return (
    <div className="container">
      <div className={styles.pageTitle}>{props.title}</div>
      <div className={styles.inputAreaWrapper}>
        <form
          onSubmit={onSubmit}
          className={state.isSubmitted ? "was-validated" : ""}
        >
          {props.error && (
            <div className={styles.errorBillboard}>{props.error}</div>
          )}
          <div className="mb-3">
            <label
              className={`form-label ${styles.required}`}
              htmlFor="last_name"
            >
              姓
            </label>
            <div className="input-group">
              <input
                type="text"
                id="last_name"
                name="last_name"
                className={
                  "form-control " +
                  (state.errorMessages["last_name"]
                    ? "is-invalid"
                    : state.last_name
                    ? "is-valid"
                    : "")
                }
                disabled={disabled}
                value={state.last_name}
                onChange={onChange}
              />
              <div className="invalid-feedback">
                {state.errorMessages["last_name"]}
              </div>
            </div>
          </div>
          <div className="mb-3">
            <label
              className={`form-label ${styles.required}`}
              htmlFor="first_name"
            >
              名
            </label>
            <div className="input-group">
              <input
                type="text"
                id="first_name"
                name="first_name"
                className={
                  "form-control " +
                  (state.errorMessages["first_name"]
                    ? "is-invalid"
                    : state.first_name
                    ? "is-valid"
                    : "")
                }
                disabled={disabled}
                value={state.first_name}
                onChange={onChange}
              />
              <div className="invalid-feedback">
                {state.errorMessages["first_name"]}
              </div>
            </div>
          </div>
          <div className="mb-3">
            <label
              className={`form-label ${styles.required}`}
              htmlFor="last_name_kana"
            >
              姓(カナ)
            </label>
            <div className="input-group">
              <input
                type="text"
                id="last_name_kana"
                name="last_name_kana"
                className={
                  "form-control " +
                  (state.errorMessages["last_name_kana"]
                    ? "is-invalid"
                    : state.last_name_kana
                    ? "is-valid"
                    : "")
                }
                disabled={disabled}
                value={state.last_name_kana}
                onChange={onChange}
              />
              <div className="invalid-feedback">
                {state.errorMessages["last_name_kana"]}
              </div>
            </div>
          </div>
          <div className="mb-3">
            <label
              className={`form-label ${styles.required}`}
              htmlFor="first_name_kana"
            >
              名(カナ)
            </label>
            <div className="input-group">
              <input
                type="text"
                id="first_name_kana"
                name="first_name_kana"
                className={
                  "form-control " +
                  (state.errorMessages["first_name_kana"]
                    ? "is-invalid"
                    : state.first_name_kana
                    ? "is-valid"
                    : "")
                }
                disabled={disabled}
                value={state.first_name_kana}
                onChange={onChange}
              />
              <div className="invalid-feedback">
                {state.errorMessages["first_name_kana"]}
              </div>
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="student_no_in_school">
              学籍番号
            </label>
            <div className="input-group">
              <input
                type="text"
                id="student_no_in_school"
                name="student_no_in_school"
                className="form-control"
                value={state.student_no_in_school || ""}
                onChange={onChange}
              />
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="email">
              メールアドレス
            </label>
            <div className="input-group">
              <input
                type="text"
                id="email"
                name="email"
                className={
                  "form-control " +
                  (state.errorMessages["email"]
                    ? "is-invalid"
                    : state.email
                    ? "is-valid"
                    : "")
                }
                disabled={disabled}
                value={state.email || ""}
                onChange={onChange}
              />
              <div className="invalid-feedback">
                {state.errorMessages["email"]}
              </div>
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="guardian_email">
              保護者のメールアドレス
            </label>
            <div className="input-group">
              <input
                type="text"
                id="guardian_email"
                name="guardian_email"
                className={
                  "form-control " +
                  (state.errorMessages["guardian_email"]
                    ? "is-invalid"
                    : state.guardian_email
                    ? "is-valid"
                    : "")
                }
                disabled={disabled}
                value={state.guardian_email || ""}
                onChange={onChange}
              />
              <div className="invalid-feedback">
                {state.errorMessages["guardian_email"]}
              </div>
            </div>
            <p className={styles.guardianEmailNote}>
              ※保護者のメールアドレスは、今後の保護者連携機能で利用予定のため、可能な限りご入力ください。
            </p>
          </div>
          <div className="mb-3">
            <label
              className={`form-label ${styles.required}`}
              htmlFor="entrance_year"
            >
              入学年度
            </label>
            <select
              name="entrance_year"
              className="form-select"
              aria-label="select"
              disabled={disabled}
              value={state.entrance_year}
              onChange={onChange}
            >
              {entranceYearSelection.map(m => {
                return (
                  <option key={m} value={m + currentYear}>
                    {m + currentYear}年
                  </option>
                );
              })}
            </select>
            <div className="invalid-feedback">
              {state.errorMessages["entracne_year"]}
            </div>
          </div>
          {(!props.isNextYear ||
            (props.student && props.student.entrance_year <= currentYear)) &&
            props.classes && (
              <>
                <div className="mb-3">
                  <label
                    className={`form-label ${styles.required}`}
                    htmlFor="current_grade"
                  >
                    現在の学年
                  </label>
                  <select
                    name="current_grade"
                    className="form-select"
                    aria-label="select"
                    disabled={disabled}
                    value={state.current_grade ?? undefined}
                    onChange={e => {
                      setState({
                        ...state,
                        class_id: 0,
                        next_grade: parseInt(e.currentTarget.value, 10) + 1,
                      });
                      onChange(e);
                    }}
                  >
                    {[1, 2, 3, 4, 5, 6].map(m => {
                      return (
                        <option key={m} value={m}>
                          {m}年
                        </option>
                      );
                    })}
                  </select>
                  <div className="invalid-feedback">
                    {state.errorMessages["current_grade"]}
                  </div>
                </div>
                <div className="mb-3">
                  <label className={`form-label`} htmlFor="class_id">
                    クラス
                  </label>
                  <div id="students" className={styles.selectArea}>
                    {props.classes
                      .filter(c => c.grade === state.current_grade)
                      .map(c => {
                        return (
                          <label className={styles.selectBtn} key={c.id}>
                            <input
                              type="checkbox"
                              name="class_id"
                              checked={state.class_id === c.id}
                              disabled={disabled}
                              value={c.id}
                              onChange={onChange}
                            />
                            <span>{c.full_name}</span>
                          </label>
                        );
                      })}
                  </div>
                </div>
                {state.errorMessages["class_id"] && (
                  <div className={styles.errorBillboard}>
                    {state.errorMessages["class_id"]}
                  </div>
                )}
              </>
            )}
          <div className="mb-3">
            <label
              className={`form-label ${styles.required}`}
              htmlFor="next_grade"
            >
              {props.isNextYear ? `${nextYearShortLabel}年度学年` : "次の学年"}
            </label>
            <select
              name="next_grade"
              className="form-select"
              aria-label="select"
              value={state.next_grade}
              onChange={onChange}
            >
              {[1, 2, 3, 4, 5, 6].map(m => {
                return (
                  <option key={m} value={m}>
                    {m}年
                  </option>
                );
              })}
            </select>
            <div className="invalid-feedback">
              {state.errorMessages["next_grade"]}
            </div>
          </div>
          {props.isNextYear && props.next_classes && (
            <>
              <label
                className={`form-label ${styles.required}`}
                htmlFor="next_klass_id"
              >
                {nextYearShortLabel}年度クラス
              </label>
              <div className={styles.selectArea}>
                {props.next_classes
                  .filter(c => c.grade === state.next_grade)
                  .map(c => (
                    <label className={styles.selectBtn} key={c.id}>
                      <input
                        type="radio"
                        name="next_klass_id"
                        checked={state.next_klass_id === c.id}
                        value={c.id}
                        onChange={onChange}
                      />
                      <span>{c.full_name}</span>
                    </label>
                  ))}
              </div>
              {state.errorMessages["next_klass_id"] && (
                <div className={styles.errorBillboard}>
                  {state.errorMessages["next_klass_id"]}
                </div>
              )}
            </>
          )}
          {props.school!.total_dlp_registrable_num! > 0 && (
            <div className="mb-3">
              <label className={`${styles.note}`}>
                ※納品指定日より前に生徒登録を行う場合、「教材対象」を無効で登録ください。納品後に改めて一括編集機能により「教材対象」の更新が可能です。
              </label>
              <span className={`${styles.label} ${styles.required}`}>
                教材対象
              </span>
              <div className="form-check form-check-inline">
                <input
                  type="radio"
                  className="form-check-input"
                  name="dlp_enabled"
                  id="dlp_enabled"
                  checked={state.dlp_enabled}
                  value={"true"}
                  onChange={onChange}
                />
                <label className="form-check-label" htmlFor="dlp_enabled">
                  有効
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  type="radio"
                  className="form-check-input"
                  name="dlp_enabled"
                  id="dlp_disabled"
                  checked={!state.dlp_enabled}
                  value={"false"}
                  onChange={onChange}
                />
                <label className="form-check-label" htmlFor="dlp_disabled">
                  無効
                </label>
              </div>
            </div>
          )}
          {props.school.total_g4s_registrable_num! > 0 && (
            <div className="mb-3">
              <span className={`${styles.label} ${styles.required}`}>
                GEAR拡張
              </span>
              <div className="form-check form-check-inline">
                <input
                  type="radio"
                  className="form-check-input"
                  name="g4s_enabled"
                  id="g4s_enabled"
                  checked={state.g4s_enabled}
                  value={"true"}
                  onChange={onChange}
                />
                <label className="form-check-label" htmlFor="g4s_enabled">
                  有効
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  type="radio"
                  className="form-check-input"
                  name="g4s_enabled"
                  id="g4s_disabled"
                  checked={!state.g4s_enabled}
                  value={"false"}
                  onChange={onChange}
                />
                <label className="form-check-label" htmlFor="g4s_disabled">
                  無効
                </label>
              </div>
            </div>
          )}
          {props.school.use_case === "cram_school" && (
            <>
              <div className="mb-3">
                <label className={`form-label ${styles.required}`}>
                  プラン
                </label>
                {state.errorMessages["plan_ids"] && (
                  <div
                    className={`text-danger ${
                      state.errorMessages["plan_ids"]
                        ? "is-invalid"
                        : "invalid-feedback"
                    }`}
                  >
                    {state.errorMessages["plan_ids"]}
                  </div>
                )}
                <div>
                  {props.student?.plan_disabled ? (
                    <label className="form-check-label">
                      「{props.student.last_name} {props.student.first_name}」
                      さんは退塾処理済みのため、プランを選択することができません。
                    </label>
                  ) : (
                    props.plans.map(plan => (
                      <div key={plan.id} className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`plan_ids`}
                          name="plan_ids"
                          value={plan.id}
                          checked={state.plan_ids.includes(plan.id)}
                          onChange={onPlanChange}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`plan_ids`}
                        >
                          {plan.name}
                        </label>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
          <button
            className="btn btn-primary"
            onClick={onSubmit}
            type="submit"
            disabled={
              Object.keys(state.errorMessages).filter(
                k => state.errorMessages[k],
              ).length > 0 || props.calling
            }
          >
            {state.id === 0 ? "登録" : "更新"}
          </button>
        </form>
        {props.student && props.deleteStudent && (
          <div className={styles.deleteArea}>
            {props.student.can_destroy ? (
              <p>まだ利用データがない場合、削除することができます</p>
            ) : (
              <p>利用データがあるため削除できません</p>
            )}
            <button
              className="btn btn-danger"
              onClick={() => {
                const { id, last_name, first_name } = props.student!;
                ctx.actions.showModal(
                  <Confirm
                    calling={props.calling}
                    closeModal={() => {
                      ctx.actions.closeModal();
                    }}
                    content={
                      <>
                        <p className="mb-0">
                          {last_name}
                          {first_name}を削除します。
                        </p>
                        <p>本当に削除してもよろしいですか？</p>
                      </>
                    }
                    variant="danger"
                    confirmText="削除"
                    callback={() => {
                      props.deleteStudent!(id);
                      ctx.actions.closeModal();
                    }}
                  />,
                );
              }}
              disabled={!props.student.can_destroy || props.calling}
            >
              削除
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentForm;
